@if (workOrder$ | async; as workOrder) {
  <elder-basic-pane-layout class="full">
    <elder-pane class="flex-none">
      <elder-pane-header>
        <elder-pane-title>
          {{ 'orders.work.order' | translate }}
        </elder-pane-title>
        <elder-pane-actions>
          <button
            mat-icon-button
            matTooltip="{{ 'orders.prod.order' | translate }}"
            (click)="goToProdOrder()"
          >
            <mat-icon>description</mat-icon>
          </button>
          @if (workOrder.scheduleInterval) {
            <app-utilisation-interval-link
              matTooltip="{{ 'manufacturing.scheduling.title' | translate }}"
              [scheduleInterval]="workOrder.scheduleInterval"
              [facility]="workOrder.manufactureFacility"
            >
            </app-utilisation-interval-link>
          }
          <button mat-icon-button (click)="saveWorkOrder()">
            <mat-icon>save</mat-icon>
          </button>
          <button mat-icon-button [matMenuTriggerFor]="menu">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #menu="matMenu">
            <button mat-menu-item (click)="deleteWorkOrder()" [disabled]="!(canDelete$ | async)">
              <mat-icon>delete</mat-icon>
              {{ 'actions.delete' | translate }}
            </button>
            <button mat-menu-item (click)="splitWorkOrder()">
              <mat-icon>copy_all</mat-icon>
              {{ 'orders.work.split' | translate }}
            </button>
            <button mat-menu-item (click)="startItemTransfer(workOrder, null)">
              <mat-icon>sync_alt</mat-icon>
              {{ 'positions.transfer' | translate }}
            </button>
            <button mat-menu-item (click)="validateScheduleInterval(workOrder)">
              <mat-icon>schedule</mat-icon>
              {{ 'positions.actions.validate-schedule' | translate }}
            </button>
            <button mat-menu-item (click)="assignProdRoutes(workOrder)">
              <mat-icon>linear_scale</mat-icon>
              {{ 'commands.assign-missing-prod-routes' | translate }}
            </button>
          </mat-menu>
        </elder-pane-actions>
      </elder-pane-header>
      <elder-pane-content class="p-0" style="overflow: hidden">
        <div class="layout-col flex">
          <div class="px-md flex-none">
            <div class="layout-row gap-sm py-lg">
              <mat-form-field elderFormFieldLabel style="width: 100px">
                <mat-label>{{ 'context.production' | translate }}</mat-label>
                <input
                  matInput
                  type="text"
                  name="facility"
                  [value]="workOrder.manufactureFacility"
                  readonly
                />
              </mat-form-field>
              <mat-form-field elderFormFieldLabel style="width: 100px">
                <mat-label>{{ 'context.collection' | translate }}</mat-label>
                <input
                  matInput
                  type="text"
                  name="collectingFacility"
                  [value]="workOrder.collectingFacility"
                  readonly
                />
              </mat-form-field>
            </div>
            <div class="layout-row gap-sm">
              <mat-form-field>
                <mat-label>{{ 'orders.state' | translate }}</mat-label>
                <elder-select
                  appWorkClearanceSelect
                  name="orderState"
                  valueAsId
                  [(value)]="workOrder.clearance"
                >
                </elder-select>
              </mat-form-field>
              <mat-form-field>
                <mat-label>{{ 'context.priority' | translate }}</mat-label>
                <elder-select
                  appOrderPrioritySelect
                  name="orderPriority"
                  valueAsId
                  [(value)]="workOrder.orderPriority"
                >
                </elder-select>
              </mat-form-field>
            </div>
            <div class="layout-row gap-sm">
              <mat-form-field>
                <mat-label>{{ 'context.release-after' | translate }}</mat-label>
                <elder-local-date-input
                  name="releaseAt"
                  [readonly]="true"
                  [(isoDateValue)]="workOrder.releaseAt"
                >
                </elder-local-date-input>
              </mat-form-field>
              <mat-form-field>
                <mat-label>{{ 'context.deadline' | translate }}</mat-label>
                <elder-local-date-input
                  name="dueAt"
                  [readonly]="true"
                  [isoDateValue]="workOrder.dueAt"
                >
                </elder-local-date-input>
              </mat-form-field>
            </div>
            <div class="layout-row gap-sm" style="margin-bottom: 21px">
              <mat-form-field
                class="flex-grow"
                [elderFormFieldLabel]="workOrder.disableAutoScheduling"
                subscriptSizing="dynamic"
              >
                <mat-label>{{ 'processing.planned' | translate }}</mat-label>
                <elder-interval-input
                  [(isoValue)]="workOrder.scheduleInterval"
                  [mode]="'day-time-range'"
                  [disabled]="!workOrder.disableAutoScheduling"
                >
                </elder-interval-input>
              </mat-form-field>
              @if (!!workOrder.leadTimeMeta && !workOrder.disableAutoScheduling) {
                <div class="align-self-center">
                  <mat-icon
                    class="primary"
                    [matTooltip]="
                      getHintKey(workOrder.leadTimeMeta)
                        | translate: getHintTranslateParams(workOrder)
                    "
                  >
                    {{ workOrder.leadTimeMeta.searchFailed ? 'warning' : 'info' }}
                  </mat-icon>
                </div>
              }
              <mat-checkbox
                class="align-self-center"
                matTooltip="{{ 'processing.automatic-scheduling' | translate }}"
                [checked]="!workOrder.disableAutoScheduling"
                [disabled]="overrideDisabled(workOrder)"
                (change)="onAutoScheduleChange($event)"
              ></mat-checkbox>
            </div>
            <app-work-order-delivery [workOrder]="workOrder"> </app-work-order-delivery>
          </div>
          <app-work-order-overview [overview]="overview$ | async" class="flex">
          </app-work-order-overview>
        </div>
      </elder-pane-content>
    </elder-pane>
    <elder-pane>
      <mat-tab-group class="flex" animationDuration="0ms" elderTabGroupRouting="workOrderDetailTab">
        <!-- Tab Items -->
        <mat-tab label="{{ 'positions.title' | translate }}" elderTab="items">
          <app-work-item-table
            class="flex"
            *matTabContent
            [workOrder]="workOrder"
            [allowEdit]="false"
            [displayTerminalLink]="true"
            [involvedProcessPlanIds]="involvedProcessPlans$ | async"
            (itemSelected)="previewItem($event)"
            (itemDeleteRequested)="deleteAllItems($event)"
          >
          </app-work-item-table>
        </mat-tab>
        <mat-tab label="{{ 'workBatches.title' | translate }}" elderTab="batches">
          <app-work-batch-browser class="flex" *matTabContent [workOrder]="workOrder.id">
          </app-work-batch-browser>
        </mat-tab>
        <mat-tab label="{{ 'packages.packages' | translate }}" elderTab="packages">
          <app-package-browser
            class="flex"
            *matTabContent
            [workOrderId]="workOrder.id"
            [company]="workOrder.company"
            [canAdd]="false"
          >
          </app-package-browser>
        </mat-tab>
      </mat-tab-group>
    </elder-pane>
  </elder-basic-pane-layout>
}
